import { useEffect, useRef, useState } from 'react'
import { motion } from 'motion/react'
import { useLanguage } from '@/context/LanguageContext'
import { translateCategory } from '@/lib/categoryColors'

interface CategoryChipsProps {
  categories: string[]
  active: string | null
  onChange: (category: string | null) => void
  counts?: Record<string, number>
}

export default function CategoryChips({ categories, active, onChange, counts }: CategoryChipsProps) {
  const { language } = useLanguage()
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const chips: Array<{ label: string; value: string | null }> = [
    { label: language === 'en' ? 'All' : 'Tutti', value: null },
    ...categories.map((c) => ({ label: translateCategory(c, language), value: c })),
  ]

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const update = () => {
      setCanScrollLeft(el.scrollLeft > 4)
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4)
    }
    update()
    el.addEventListener('scroll', update, { passive: true })
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => {
      el.removeEventListener('scroll', update)
      observer.disconnect()
    }
  }, [chips.length])

  return (
    <div className="relative">
      <div
        ref={scrollRef}
        className="flex justify-start items-center gap-2 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {chips.map((chip) => {
          const isActive = active === chip.value
          const count = chip.value === null ? undefined : counts?.[chip.value]
          return (
            <button
              key={chip.label}
              onClick={() => onChange(chip.value)}
              className={`relative shrink-0 inline-flex items-center h-10 rounded-full px-4 text-sm font-semibold transition-colors ${
                isActive
                  ? 'text-white'
                  : 'glass text-neutral-500 hover:text-neutral-900'
              }`}
            >
              {isActive && (
                <motion.span
                  layoutId="activeChip"
                  className="absolute inset-0 bg-neutral-900 rounded-full"
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}
              <span className="relative z-10 inline-flex items-center gap-1.5">
                {chip.label}
                {count != null && (
                  <span
                    className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${
                      isActive ? 'bg-white/25 text-white' : 'bg-neutral-200 text-neutral-700'
                    }`}
                  >
                    {count}
                  </span>
                )}
              </span>
            </button>
          )
        })}
      </div>
      {/* Left fade — visible when scrolled right */}
      <div
        className={`pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-background via-background/60 to-transparent transition-opacity duration-200 ${
          canScrollLeft ? 'opacity-100' : 'opacity-0'
        }`}
      />
      {/* Right fade — always visible to soften the edge toward adjacent elements */}
      <div
        className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-background via-background/60 to-transparent"
      />
    </div>
  )
}
